import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LaunchDarklyService } from '../../services/launchdarkly.service';
import { StorageService } from '../../services/storage.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { Roles } from 'src/app/shared/enums/role.enum';
import {
  AddEmailPayload,
  ApplicationData,
  ApplicationTemplatePayload,
  CheckDomainValidityResponse,
  CreateApplicationPayload,
  DomainPayload,
  DomainResponse,
  UserApplication,
  UserEmail,
} from '../_models/application.models';
import { RequestCompanyDatatable } from '../_models/company.model';
import { Datatable } from '../_models/report.models';
import { ApplicationMetric } from '../_models/settings.models';
import { Template } from '../_models/template.model';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  private baseURL = `${environment.apiUrl}/api`;
  private APPLICATIONS_URL = '/application/getapplicationbyuser';
  private CREATE_COMPANY_URL = '/application';
  private APPLICATION_DATA_URL = '/applicationdata?applicationId=';
  private EMAIL_URL = '/emails?applicationId=';
  private IP_TRACKING_DATATABLE_URL = '/application/getiptrackingdatatable';
  private APP_METRIC = '/application/metrics';
  private GET_APPLICATION_USERS_DATATABLE_URL = '/user/getdatatable';
  private TEMPLATE_URL = '/template';
  private CREATE_DOMAIN = '/application/createdomain';
  private CHECK_DOMAIN_STATUS = '/application/checkstatus';
  private GET_TIME_ZONES = '/application/getalltimezones';
  private GET_LANGUAGES = '/application/getalllanguages';
  private RENAME_APLICATION = '/application/renameapplication';
  private readonly GET_SUBDOMAINS_URL = '/application/getsubdomains';

  public get createApplicationUrl() {
    return `${this.baseURL}${this.CREATE_COMPANY_URL}`;
  }
  public get renameApplicationUrl() {
    return `${this.baseURL}${this.RENAME_APLICATION}`;
  }

  public get applicationsUrl() {
    return `${this.baseURL}${this.APPLICATIONS_URL}`;
  }

  public get applicationDataUrl() {
    return `${this.baseURL}${this.APPLICATION_DATA_URL}`;
  }

  public get emailsUrl() {
    return `${this.baseURL}${this.CREATE_COMPANY_URL}${this.EMAIL_URL}`;
  }

  public get templateUrl() {
    return `${this.baseURL}${this.TEMPLATE_URL}`;
  }
  public get createDomainUrl() {
    return `${this.baseURL}${this.CREATE_DOMAIN}`;
  }
  public get checkDomainStatusurl() {
    return `${this.baseURL}${this.CHECK_DOMAIN_STATUS}`;
  }

  public get getTimeZoneUrl() {
    return `${this.baseURL}${this.GET_TIME_ZONES}`;
  }

  public get getLanguagesUrl() {
    return `${this.baseURL}${this.GET_LANGUAGES}`;
  }

  public get getSubdomainsUrl() {
    return `${this.baseURL}${this.GET_SUBDOMAINS_URL}`;
  }

  isSingleAppUser$ = new BehaviorSubject<boolean | null>(null);

  /**
   * When `true`, show the App Library link in sidebars.
   * Gated by `FEATURE_FLAGS.ENABLE_SINGLE_APP_DIRECT_LOGIN` (`product-gab-ui-single-app-direct-login`).
   */
  readonly showAppLibraryInSidebar$: Observable<boolean>;

  /** Clears single-app UI state (call on logout and before re-resolving after account switch). */
  clearSingleAppUserState(): void {
    this.isSingleAppUser$.next(null);
  }

  constructor(
    private readonly http: HttpClient,
    private readonly ldService: LaunchDarklyService,
    private readonly storage: StorageService
  ) {
    // Drive sidebar visibility from isSingleAppUser$ only — reading the LD flag
    // synchronously via getFlagValueSync() each time isSingleAppUser$ emits.
    // Using getFlagValue() (BehaviorSubject) here is unsafe because NavigationService
    // also calls getFlagValue() in its constructor during the HTTP-interceptor injection
    // chain, before waitForInitialization() completes, seeding the shared BehaviorSubject
    // with false. By the time isSingleAppUser$ emits a resolved value (post-login),
    // APP_INITIALIZER has completed and getFlagValueSync() returns the correct live value.
    this.showAppLibraryInSidebar$ = this.isSingleAppUser$.pipe(
      map((single) => {
        const isSingleAppDirectLoginEnabled = this.ldService.getFlagValueSync(
          FEATURE_FLAGS.ENABLE_SINGLE_APP_DIRECT_LOGIN, false
        );
        return !isSingleAppDirectLoginEnabled || single !== true;
      }),
      distinctUntilChanged()
    );
  }

  /**
   * Resolves post-login destination when the user would otherwise go to /apps only.
   * No-ops the single-app probe when `FEATURE_FLAGS.ENABLE_SINGLE_APP_DIRECT_LOGIN` is off.
   *
   * Clears `isSingleAppUser$` to `null` first so nav/sidebar drop any stale true/false from a prior session.
   * Then:
   * - `deferred`: flag off, impersonating, or super admin (no probe). State stays `null`.
   * - `library`: probed user has 0 or 2+ apps. Sets `isSingleAppUser$` to `false`.
   * - `{ appKey }`: probed user has exactly one app. Sets `isSingleAppUser$` to `true`.
   *
   * @param options.isSuperAdmin When provided (e.g. from Home), super-admin bypass uses this callback;
   *   otherwise `storage.getUserRole()` is used.
   */
  resolveSingleAppPostLogin$(options?: {
    isSuperAdmin?: () => boolean;
  }): Observable<'deferred' | 'library' | { appKey: string }> {
    // Read the flag synchronously from the live LD client rather than a BehaviorSubject.
    // This avoids a race condition where NgRx effects eagerly instantiate ApplicationService
    // before APP_INITIALIZER runs, causing the BehaviorSubject to be seeded with the default
    // value (false) instead of the real LD value. By the time this method is called
    // (post-login), APP_INITIALIZER has completed and the LD client is fully initialized.
    const isSingleAppDirectLoginEnabled = this.ldService.getFlagValueSync(FEATURE_FLAGS.ENABLE_SINGLE_APP_DIRECT_LOGIN, false);
    this.clearSingleAppUserState();
    if (!isSingleAppDirectLoginEnabled) {
      return of('deferred' as const);
    }
    const isSuperAdmin = options?.isSuperAdmin ? options.isSuperAdmin() : this.storage.getUserRole() === Roles.superAdmin;
    const isImpersonating = !!this.storage.getImpersonateId();
    if (isImpersonating || isSuperAdmin) {
      return of('deferred' as const);
    }
    return this.getApplicationsbyUser({ skip: 0, size: 2 }).pipe(
      map((apps) => {
        if (apps?.length === 1) {
          this.isSingleAppUser$.next(true);
          return { appKey: apps[0].Key } as const;
        }
        this.isSingleAppUser$.next(false);
        return 'library' as const;
      }),
      catchError((_err) => {
        this.isSingleAppUser$.next(false);
        return of('library' as const);
      })
    );
  }

  /**
   * Get application by current user
   */
  getApplicationsbyUser = (
    params: object = {}
  ): Observable<Array<UserApplication>> => {
    return this.http.post<Array<UserApplication>>(this.applicationsUrl,params);
  };

  /**
   *
   * Get application by Id
   */
  getApplicationById = (
    appId: string,
    fromSettings: boolean = false
  ): Observable<UserApplication> =>
    this.http.get<UserApplication>(
      `${this.baseURL}${this.CREATE_COMPANY_URL}?id=${appId}&fromSettings=${fromSettings}`,
      {}
    );

  /**
   *
   * Get application metric by Id
   */
  getAppMetric = (appId: string): Observable<ApplicationMetric> =>
    this.http.get<ApplicationMetric>(
      `${this.baseURL}${this.APP_METRIC}?id=${appId}`,
      {}
    );

  /**
   * Create application
   * @param payload request body
   */
  createApplication = (
    payload: CreateApplicationPayload
  ): Observable<UserApplication> => {
    return this.http.post<UserApplication>(
      this.createApplicationUrl,
      payload,
      {}
    );
  };

  /**
   * Create application
   * @param payload request body
   */
  updateApplication = (payload: any): Observable<any> => {
    return this.http.put<any>(this.createApplicationUrl, payload, {});
  };

  /**
   * Get application by current user
   */
  getApplicationData = (tableId: string): Observable<ApplicationData> =>
    this.http.get<ApplicationData>(`${this.applicationDataUrl}${tableId}`, {});

  /**
   * Upload a logo
   */
  uploadLogo = (fd: any): Observable<any> => {
    return this.http.post(`${this.baseURL}/application/uploadlogo`, fd, {
      // headers: headers,
    });
  };

  /**
   * Create a template
   */
  createTemplate = (payload: ApplicationTemplatePayload): Observable<any> => {
    return this.http.post<Template>(
      this.templateUrl,
      payload,
      {}
    );
  }

  /**
   * Update a template
   */
  updateTemplate = (payload: ApplicationTemplatePayload): Observable<any> => {
    return this.http.put<Template>(this.templateUrl, payload, {});
  }

  /**
   * Get email of users
   */
  getEmails = (
    applicationId: string,
    includeDefault: boolean
  ): Observable<Array<UserEmail>> =>
    this.http.get<Array<UserEmail>>(
      `${this.emailsUrl}${applicationId}&includeDefault=${includeDefault}`,
      {}
    );

  /**
   * Add an Email
   */
  addEmail = (payload: AddEmailPayload): Observable<any> =>
    this.http.post<any>(
      `${this.baseURL}${this.CREATE_COMPANY_URL}/email`,
      payload
    );

  /**
   * Get ip tracking users datatable
   */
  getIPTrackingUsersDatatable = (
    payload: RequestCompanyDatatable
  ): Observable<Datatable> =>
    this.http.post<Datatable>(
      `${this.baseURL}${this.IP_TRACKING_DATATABLE_URL}`,
      payload,
      {}
    );

  /**
   * Get application users datatable
   */
  getApplicationUsersDatatable = (
    payload: RequestCompanyDatatable
  ): Observable<Datatable> =>
    this.http.post<Datatable>(
      `${this.baseURL}${this.GET_APPLICATION_USERS_DATATABLE_URL}`,
      payload,
      {}
    );

  /**
   * Get templates by application id
   */
  getApplicationTemplates = (appId: string, docTemplate: boolean = false, geoJsonTemplate:boolean = false): Observable<Array<Template>> =>
    this.http.get<Array<Template>>(`${this.templateUrl}/getbyapplicationid?id=${appId}&docTemplate=${docTemplate}&geoJson=${geoJsonTemplate}`, {});

    /**
   * Get template by id
   */
  getApplicationTemplateById = (templateId: string): Observable<Template> =>
    this.http.get<Template>(`${this.templateUrl}?id=${templateId}`, {});

  /**
   * delete template
   */
  deleteTemplate = (templateId: string): Observable<void> =>
    this.http.delete<void>(`${this.templateUrl}?templateId=${templateId}`, {});

    /**
     * Check Domain Validity
     */
    createDomain = (payload:DomainPayload):Observable<DomainResponse> =>
        this.http.post<DomainResponse>(`${this.createDomainUrl}`,payload);

  /**
   * Check Domain Validity
   */
  checkDomainStatus=(payload:DomainPayload):Observable<DomainResponse> =>
      this.http.get<DomainResponse>(`${this.checkDomainStatusurl}?id=${payload.Key}`);
  /**
   * Load TimeZone Data
   */
  getTimeZones=():Observable<any> =>
      this.http.get<any>(`${this.getTimeZoneUrl}`);
  /**
   * Get All Languages
   */
  getLanguages=():Observable<any> =>
      this.http.get<any>(`${this.getLanguagesUrl}`);

  /**
   * Delete Application
   */
  deleteApplication = (appId: string): Observable<void> =>
    this.http.delete<void>(
      `${this.createApplicationUrl}?id=${appId}`,
      {}
    );

  /**
   * Rename Application
   */
  renameApplication = (payload:any): Observable<void> =>
    this.http.put<void>(
      `${this.renameApplicationUrl}`,
      payload
    );

  getSubdomains = (): Observable<any> =>
    this.http.get<any>(this.getSubdomainsUrl);
}
